/**
 * EntityEditForm — DSL 驱动的表单
 * 支持字段分组、验证规则、类型推断
 */

import { useState, useCallback, useId } from 'react';
import type { EntityMeta, FieldDef } from '@/types/entity';
import { useTranslation } from 'react-i18next';
import { useViewTemplate, type FormFieldDSL } from '@/lib/use-view-template';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { Save, X, Loader2 } from 'lucide-react';
import { LookupField } from '@/components/form/LookupField';
import { summarizeValue } from '@/lib/format';

/** Auto-generate a document number: SLUG_PREFIX-YYMM-RANDOM */
function generateDocNumber(slug: string): string {
  const prefix = slug
    .replace(/s$/, '')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 4);
  const now = new Date();
  const yymm = `${String(now.getFullYear()).slice(2)}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${yymm}-${rand}`;
}

/** Fields that should be auto-generated on create */
const AUTO_CODE_FIELDS = new Set([
  'code',
  'docNo',
  'refNo',
  'number',
  'jobNumber',
  'shipmentNumber',
  'entryNumber',
  'documentNumber',
]);

const TEMPLATE_SOURCE_LABEL: Record<string, string> = {
  screen_template: 'Screen Override',
  classic_template: 'Classic Template',
  tenant_view_template: 'Tenant Template',
  domain_template_library: 'Domain Template',
  generic_meta_fallback: 'Meta Fallback',
  none: 'No Template',
};

interface EntityEditFormProps {
  meta: EntityMeta;
  slug: string;
  templateSlug?: string;
  /** null = create mode; Record = edit mode */
  record: Record<string, unknown> | null;
  onSaved: (record: Record<string, unknown>) => void;
  onCancel: () => void;
  /** Restrict form to a subset of fields for quick-edit surfaces like header cards */
  fieldNames?: string[];
  /** Prefill values for create/child forms */
  initialValues?: Record<string, unknown>;
  /** Keep fields in payload but hide them from rendered form */
  hiddenFieldNames?: string[];
  /** Grid columns for the form layout (default: 6) */
  columns?: number;
  /** Keep action bar visible while the form body scrolls inside slide/dock panels */
  stickyActions?: boolean;
  /** Force template source badge visibility for partial quick-edit surfaces */
  showTemplateSourceBadge?: boolean;
  /** Use a custom submit path or business rule instead of generic /entities CRUD */
  submitOverride?: (args: {
    isEdit: boolean;
    slug: string;
    record: Record<string, unknown> | null;
    payload: Record<string, unknown>;
  }) => Promise<Record<string, unknown>>;
}

export function EntityEditForm({
  meta,
  slug,
  templateSlug,
  record,
  onSaved,
  onCancel,
  fieldNames,
  initialValues,
  hiddenFieldNames,
  columns,
  stickyActions = false,
  showTemplateSourceBadge = false,
  submitOverride,
}: EntityEditFormProps) {
  const isEdit = record !== null;
  const toast = useToast();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { template, source: templateSource, templateId } = useViewTemplate(templateSlug ?? slug, 'form', meta);
  const forceEnglishUi = (templateSlug ?? slug).startsWith('oi-');
  const formSaveLabel = forceEnglishUi ? 'Save' : t('form.save');
  const formCreateLabel = forceEnglishUi ? 'Create' : t('form.create');
  const formCancelLabel = forceEnglishUi ? 'Cancel' : t('form.cancel');
  const isAdmin = user?.role === 'admin' || user?.role === 'platform_admin';
  const showTemplateSource = isAdmin && templateSource && templateSource !== 'none' && (!fieldNames || showTemplateSourceBadge);
  const templateSourceLabel = TEMPLATE_SOURCE_LABEL[templateSource ?? 'none'] ?? (templateSource ?? 'Unknown');

  const dslFields: FormFieldDSL[] | null = template?.fields ?? null;

  const editableFields = meta.fields.filter((field) => {
    if (field.name === 'tenantId' || field.name === 'id') return false;
    if (isEdit && !field.updatable) return false;
    if (field.type === 'datetime' && (field.name === 'createdAt' || field.name === 'updatedAt')) return false;
    if (fieldNames && !fieldNames.includes(field.name)) return false;
    return true;
  });

  const visibleEditableFields = editableFields.filter((field) => !hiddenFieldNames?.includes(field.name));

  const orderedFields: FieldDef[] = dslFields
    ? dslFields
        .filter((dslField) => dslField.type !== 'hidden')
        .map((dslField) => visibleEditableFields.find((editableField) => editableField.name === dslField.field))
        .filter((field): field is FieldDef => field != null)
    : visibleEditableFields;

  const groups = groupFields(dslFields, orderedFields);

  const initial: Record<string, string> = {};
  for (const field of editableFields) {
    let value = initialValues?.[field.name] ?? record?.[field.column ?? field.name];
    if (!isEdit && value == null && AUTO_CODE_FIELDS.has(field.name)) {
      value = generateDocNumber(slug);
    }
    initial[field.name] = normalizeFieldValue(field, value);
  }

  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = useCallback((fieldName: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    for (const field of orderedFields) {
      const dsl = dslFields?.find((dslField) => dslField.field === field.name);
      const value = values[field.name] ?? '';

      if ((dsl?.required ?? field.required) && value.trim() === '') {
        errors[field.name] = forceEnglishUi
          ? `${field.label ?? field.name} is required`
          : t('form.required', { field: field.label ?? field.name });
      }

      if (dsl?.validation && value.trim() !== '') {
        const { min, max, pattern, message } = dsl.validation;
        const num = Number(value);
        if (min !== undefined && !Number.isNaN(num) && num < min) {
          errors[field.name] = message ?? t('form.minValue', { min });
        }
        if (max !== undefined && !Number.isNaN(num) && num > max) {
          errors[field.name] = message ?? t('form.maxValue', { max });
        }
        if (pattern) {
          try {
            if (!new RegExp(pattern).test(value)) {
              errors[field.name] = message ?? t('form.invalidFormat');
            }
          } catch {
            // Skip invalid regex from DSL.
          }
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [dslFields, forceEnglishUi, orderedFields, t, values]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError(null);

    const payload: Record<string, unknown> = {};
    for (const field of editableFields) {
      const value = values[field.name];
      if (isEdit && value === initial[field.name]) continue;
      if (value === '' && field.nullable !== false) {
        payload[field.name] = null;
      } else if (field.type === 'integer') {
        payload[field.name] = parseInt(value ?? '0', 10) || 0;
      } else if (field.type === 'number' || field.type === 'decimal' || field.type === 'money') {
        payload[field.name] = parseFloat(value ?? '0') || 0;
      } else if (field.type === 'boolean') {
        payload[field.name] = value === 'true' || value === '1';
      } else {
        payload[field.name] = value;
      }
    }

    try {
      let result: Record<string, unknown>;
      if (submitOverride) {
        result = await submitOverride({ isEdit, slug, record, payload });
      } else if (isEdit) {
        result = await api.patch<Record<string, unknown>>(`/entities/${slug}/${record!.id}`, payload);
      } else {
        result = await api.post<Record<string, unknown>>(`/entities/${slug}`, payload);
      }
      toast.success(
        forceEnglishUi
          ? (isEdit ? 'Record saved' : 'Record created')
          : (isEdit ? t('form.saveSuccess') : t('form.createSuccess')),
      );
      onSaved(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : (forceEnglishUi ? 'Save failed' : t('form.saveFailed'));
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [editableFields, forceEnglishUi, initial, isEdit, onSaved, record, slug, submitOverride, t, toast, validate, values]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showTemplateSource && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge
            variant="outline"
            className="text-[10px] font-medium uppercase tracking-wide"
            title={`Template source: ${templateSource}`}
          >
            {templateSourceLabel}
          </Badge>
          {templateId && (
            <Badge
              variant="secondary"
              className="max-w-[180px] truncate font-mono text-[10px]"
              title={templateId}
            >
              {templateId}
            </Badge>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {groups.map((group) => (
        <fieldset key={group.label} className="space-y-3">
          {group.label !== '_default' && (
            <legend className="mb-2 border-b pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </legend>
          )}
          <div className={`grid grid-cols-2 gap-4 ${getFormGridClass(columns)}`}>
            {group.fields.map((field) => {
              const dsl = dslFields?.find((dslField) => dslField.field === field.name);
              return (
                <div key={field.name} className={getFieldSpanClass(dsl?.span)}>
                  <FormField
                    field={field}
                    dsl={dsl ?? null}
                    value={values[field.name] ?? ''}
                    error={fieldErrors[field.name]}
                    onChange={(value) => handleChange(field.name, value)}
                  />
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}

      <div
        className={stickyActions
          ? 'sticky bottom-0 z-10 -mx-1 flex items-center justify-end gap-2 border-t bg-background/95 px-1 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80'
          : 'flex items-center justify-end gap-2 border-t pt-2'}
      >
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          <X className="mr-1.5 h-3.5 w-3.5" />
          {formCancelLabel}
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={saving}
          style={{ background: 'var(--category-indicator)', borderColor: 'var(--category-indicator)', color: 'white' }}
        >
          {saving ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="mr-1.5 h-3.5 w-3.5" />
          )}
          {isEdit ? formSaveLabel : formCreateLabel}
        </Button>
      </div>
    </form>
  );
}

function getFormGridClass(columns?: number): string {
  switch (columns ?? 6) {
    case 2:
      return 'lg:grid-cols-2';
    case 3:
      return 'lg:grid-cols-3';
    case 4:
      return 'lg:grid-cols-4';
    case 5:
      return 'lg:grid-cols-5';
    case 6:
    default:
      return 'lg:grid-cols-6';
  }
}

function getFieldSpanClass(span?: number): string | undefined {
  const safeSpan = Math.min(Math.max(span ?? 1, 1), 2);
  if (safeSpan === 2) return 'col-span-2 lg:col-span-2';
  return undefined;
}

interface FieldGroup {
  label: string;
  fields: FieldDef[];
}

function groupFields(dslFields: FormFieldDSL[] | null, ordered: FieldDef[]): FieldGroup[] {
  if (!dslFields) {
    return [{ label: '_default', fields: ordered }];
  }

  const groupMap = new Map<string, FieldDef[]>();
  for (const field of ordered) {
    const dsl = dslFields.find((dslField) => dslField.field === field.name);
    const group = dsl?.group ?? '_default';
    const existing = groupMap.get(group);
    if (existing) {
      existing.push(field);
    } else {
      groupMap.set(group, [field]);
    }
  }

  return Array.from(groupMap.entries()).map(([label, fields]) => ({ label, fields }));
}

function FormField({
  field,
  dsl,
  value,
  error,
  onChange,
}: {
  field: FieldDef;
  dsl: FormFieldDSL | null;
  value: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const labelId = `${inputId}-label`;
  const errorId = `${inputId}-error`;
  const inputType = getInputType(field, dsl);
  const label = dsl?.label ?? field.label ?? field.name;
  const placeholder = dsl?.placeholder ?? label;
  const required = dsl?.required ?? field.required;
  const options = dsl?.options ?? field.enumValues?.map((v) => ({ label: v, value: v }));
  const normalizedOptions = (options ?? []).map((option) => ({
    label: typeof option.label === 'string' ? option.label : summarizeValue(option.label),
    value: typeof option.value === 'string' ? option.value : String(option.value ?? ''),
  }));

  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} id={labelId} className="text-xs font-medium text-muted-foreground">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </label>

      {(dsl?.type === 'select' || (field.type === 'enum' && options)) ? (
        <select
          id={inputId}
          name={field.name}
          aria-labelledby={labelId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={selectClass(error)}
        >
          <option value="">---</option>
          {normalizedOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : field.type === 'status' && field.enumValues ? (
        <div className="flex flex-wrap gap-1">
          {field.enumValues.map((enumValue) => (
            <Badge
              key={enumValue}
              variant={enumValue === value ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => onChange(enumValue)}
            >
              {enumValue}
            </Badge>
          ))}
        </div>
      ) : (dsl?.type === 'lookup' || (field.type === 'uuid' && field.refSlug)) ? (
        <LookupField
          entitySlug={field.refSlug!}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          error={!!error}
          triggerId={inputId}
          name={field.name}
          ariaLabelledBy={labelId}
        />
      ) : (dsl?.type === 'toggle' || field.type === 'boolean') ? (
        <div className="flex items-center gap-2">
          <input
            id={inputId}
            name={field.name}
            type="checkbox"
            aria-labelledby={labelId}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? errorId : undefined}
            checked={value === 'true' || value === '1'}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
            className="h-4 w-4"
          />
          <span className="text-sm">
            {value === 'true' || value === '1' ? t('form.boolTrue', 'True') : t('form.boolFalse', 'False')}
          </span>
        </div>
      ) : (dsl?.type === 'textarea' || field.type === 'text' || field.type === 'json') ? (
        <textarea
          id={inputId}
          name={field.name}
          aria-labelledby={labelId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={textareaClass(error)}
          placeholder={placeholder}
        />
      ) : (
        <Input
          id={inputId}
          name={field.name}
          aria-labelledby={labelId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          step={field.type === 'decimal' || field.type === 'money' ? '0.01' : undefined}
          className={error ? 'border-destructive focus-visible:ring-destructive' : ''}
        />
      )}

      {error && <p id={errorId} className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function normalizeFieldValue(field: FieldDef, value: unknown): string {
  if (value == null) return '';

  if ((field.type === 'uuid' || field.refSlug) && typeof value === 'object' && value !== null) {
    const id = (value as Record<string, unknown>).id;
    if (typeof id === 'string') return id;
  }

  if (field.type === 'json') {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  }

  if (field.type === 'datetime' && typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 16);
  }

  if (typeof value === 'object') {
    return summarizeValue(value);
  }

  return String(value);
}

function selectClass(error?: string): string {
  const base = 'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1';
  return error
    ? `${base} border-destructive focus-visible:ring-destructive`
    : `${base} border-input focus-visible:ring-ring`;
}

function textareaClass(error?: string): string {
  const base = 'flex w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1';
  return error
    ? `${base} border-destructive focus-visible:ring-destructive`
    : `${base} border-input focus-visible:ring-ring`;
}

function getInputType(field: FieldDef, dsl: FormFieldDSL | null): string {
  if (dsl?.type === 'number') return 'number';
  if (dsl?.type === 'date') return 'date';
  switch (field.type) {
    case 'integer':
    case 'decimal':
    case 'money':
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime-local';
    default:
      return 'text';
  }
}
